import { Button, CircularProgress } from '@civilgrid/ui/components';
import { isEmpty } from 'lodash';
import type React from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import { updateAgency as apiUpdateAgency, type CivilSearcherResponse } from '~/api/civilsearch';
import { type Agency, CivilSearchTier, ContactMethod } from '~/common/types';
import ControlledCheckbox from '~/components2/forms/controlled/ControlledCheckbox';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import ControlledTextArea from '~/components2/forms/controlled/ControlledTextArea';
import Form from '~/components2/forms/Form';
import Radio from '~/components2/forms/Radio';
import { createPositiveIntegerValidator } from '~/components2/forms/validators';
import InfoTooltip from '~/components2/InfoTooltip';
import { Notify } from '~/components2/Notify';
import FormSection from '~/pages/admin/civilsearch/common/FormSection';
import { getEstimatedResponseTimeLabel } from '~/pages/projectDetails/utilitiesV2/common/estimatedResponseTime';
import store from '../../store';
import AgencyLinks from './AgencyLinks';
import AgencyLogo from './AgencyLogo';
import StaticInfo from './StaticInfo';

const validatePositiveInteger = createPositiveIntegerValidator({});

import { useQuery } from '@tanstack/react-query';
import { getCivilSearchers } from '~/api/civilsearch';
import { civilSearchTierLabels } from '~/common/dataRequestUtils';
import ControlledSelect from '~/components2/forms/controlled/ControlledSelect';

type TFormValues = {
  doesNotRespond: boolean;
  chargesMapFees: boolean;
  requiresLetterhead: boolean;
  requiresNda: boolean;
  hideResponseEstimate: boolean;
  averageMapFeePerPage: number;
  mapFeeNote: string;
  preferredContactMethod: ContactMethod;
  prrUrl: string;
  portalContactFormUrl: string;
  notes: string;
  urbanRestriction: number;
  ruralRestriction: number;
  firstFollowUpDuration: number;
  secondFollowUpDuration: number;
  thirdFollowUpDuration: number;
  fourthFollowUpDuration: number;
  timeoutDuration: number;
  civilSearchTier: CivilSearchTier;
  defaultCivilSearchAssignee?: string;
};

const civilSearchTierOptions = Object.values(CivilSearchTier).map((value) => ({
  value,
  label: <div className="text-xs">{civilSearchTierLabels[value]}</div>,
}));

const numberFields: (keyof TFormValues)[] = ['averageMapFeePerPage'];

const isNumberField = (v: keyof TFormValues) => {
  if (numberFields.includes(v)) {
    return true;
  }

  return false;
};

const contactInfoMap: { [key in ContactMethod]: string } = {
  [ContactMethod.Email]: 'Email',
  [ContactMethod.PublicRecordsRequest]: 'PRR',
  [ContactMethod.PortalContactForm]: 'Portal Contact Form',
} as const;

export default function General() {
  const { agency } = useSnapshot(store) as typeof store;

  // Fetch civil searchers for the default assignee dropdown
  const { data: civilSearchers = [], isFetching } = useQuery({
    queryKey: ['civilSearchers'],
    queryFn: getCivilSearchers,
  });

  if (isFetching) return <CircularProgress indeterminate />;
  if (!agency) return null;

  return <GeneralContent agency={agency} civilSearchers={civilSearchers} />;
}

function GeneralContent({ agency, civilSearchers }: { agency: Agency; civilSearchers: CivilSearcherResponse[] }) {
  const designationItems: { value: string; label: React.ReactElement }[] = civilSearchers.map((searcher) => ({
    value: searcher.email,
    label: (
      <div>
        <div className="text-xs font-medium">{searcher.name}</div>
        <div className="text-2xs text-gray-500">{searcher.email}</div>
      </div>
    ),
  }));

  const form = useForm<TFormValues>({
    defaultValues: {
      doesNotRespond: agency?.doesNotRespond,
      chargesMapFees: agency?.chargesMapFees,
      requiresLetterhead: agency?.requiresLetterhead,
      requiresNda: agency?.requiresNda,
      hideResponseEstimate: agency?.hideResponseEstimate,
      averageMapFeePerPage: agency?.averageMapFeePerPage,
      mapFeeNote: agency?.mapFeeNote,
      preferredContactMethod: agency?.preferredContactMethod,
      prrUrl: agency?.prrUrl,
      portalContactFormUrl: agency?.portalContactFormUrl,
      notes: agency?.notes,
      urbanRestriction: Number(agency?.restrictions?.urban),
      ruralRestriction: Number(agency?.restrictions?.rural),
      firstFollowUpDuration: agency?.firstFollowUpDuration,
      secondFollowUpDuration: agency?.secondFollowUpDuration,
      thirdFollowUpDuration: agency?.thirdFollowUpDuration,
      fourthFollowUpDuration: agency?.fourthFollowUpDuration,
      timeoutDuration: agency?.timeoutDuration,
      civilSearchTier: agency?.civilSearchTier,
      defaultCivilSearchAssignee: agency?.defaultCivilSearchAssignee?.email ?? '',
    },
  });

  const onSubmit: SubmitHandler<TFormValues> = async (data) => {
    const properties: { [key: string]: any } = {};

    for (const key in data) {
      const k = key as keyof TFormValues;
      if (form.formState.dirtyFields[k]) {
        const v = data[k];
        if (isNumberField(k)) {
          properties[k] = Number(v);
        } else {
          properties[k] = v;
        }
      }
    }

    if (isEmpty(properties)) {
      console.warn('No properties to be updated, invalid submit.');
      return;
    }

    if (form.formState.dirtyFields.urbanRestriction || form.formState.dirtyFields.ruralRestriction) {
      properties.restrictions = {
        urban: data.urbanRestriction,
        rural: data.ruralRestriction,
      };
    }

    try {
      const a = await apiUpdateAgency(agency!.id, properties);
      store.agency = a;

      form.reset(undefined, { keepValues: true });

      Notify.info({
        title: 'Update Agency',
        message: 'Agency successfully updated',
      });

      form.reset(undefined, { keepValues: true });
    } catch {
      Notify.error({
        title: 'Update Agency',
        message: 'An error occurred while updating the agency.',
      });
    }
  };

  if (!agency) return null;
  const isDisabled = !form.formState.isDirty;
  // Ungated: the rep always sees the underlying estimate, even when it is hidden from customers.
  const estimateLabel = getEstimatedResponseTimeLabel(agency);

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="space-y-4 pb-20">
        <Button type="submit" disabled={isDisabled}>
          Save Agency
        </Button>

        <StaticInfo agency={agency} />

        <FormSection title="CivilSearch Information">
          <div className="grid grid-cols-3 gap-2">
            <ControlledSelect
              control={form.control}
              name="civilSearchTier"
              label="CivilSearch Tier"
              items={civilSearchTierOptions}
            />
            <ControlledSelect
              control={form.control}
              name="defaultCivilSearchAssignee"
              label="Default CivilSearch Request Assignee"
              items={designationItems}
              isClearable
            />
          </div>
        </FormSection>

        <FormSection title="Agency Submission Requirements">
          <ControlledCheckbox name="doesNotRespond" control={form.control} label="Agency does not respond" />

          <ControlledCheckbox
            name="requiresLetterhead"
            control={form.control}
            label="Agency requires letterhead with project scope to process requests"
          />

          <ControlledCheckbox name="chargesMapFees" control={form.control} label="Agency charges map fees" />

          <ControlledCheckbox name="requiresNda" control={form.control} label="Agency requires NDA" />
        </FormSection>

        <FormSection title="Estimated Response Time">
          <div className="flex flex-col space-y-2">
            <div className="text-xs" data-testid="agency-estimated-response-time">
              <span className="text-gray-500">Current estimate shown to customers: </span>
              <span className="font-medium">{estimateLabel ?? 'No estimate available yet'}</span>
            </div>
            <ControlledCheckbox
              name="hideResponseEstimate"
              control={form.control}
              data-testid="agency-hide-response-estimate-checkbox"
              label="Hide estimated response time from customers"
            />
          </div>
        </FormSection>

        <FormSection title="Agency Cost and Contact Information">
          <div className="flex flex-row space-x-4">
            <div>
              <div className="flex items-start space-x-1 px-1.5 text-2xs uppercase">
                <span>Preferred Contact Method</span>
                <InfoTooltip text="The agency's preferred method of contact" label="About preferred contact method" />
              </div>
              <Controller
                name="preferredContactMethod"
                control={form.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <div className="">
                    <Radio
                      {...field}
                      value={field.value}
                      items={[
                        ...Object.values(ContactMethod).map((method) => ({
                          value: method,
                          label: <div>{contactInfoMap[method]}</div>,
                        })),
                      ]}
                    />
                  </div>
                )}
              />
            </div>

            <div>
              <ControlledInput
                label="Average Map Fees Per Page"
                control={form.control}
                name="averageMapFeePerPage"
                isRequired={false}
                tooltipText="Average map fee per page"
                type="number"
              />
              <ControlledInput
                label="Map Fee Note"
                control={form.control}
                name="mapFeeNote"
                isRequired={false}
                tooltipText="Eg. Charges $200 + per page."
              />

              <ControlledInput
                name="prrUrl"
                control={form.control}
                label="Public Records Request URL"
                isRequired={false}
                tooltipText="Link to the agency's public records request page"
                canCopy
              />

              <ControlledInput
                name="portalContactFormUrl"
                control={form.control}
                label="Portal Contact Form URL"
                isRequired={false}
                tooltipText="Link to the agency's portal contact form page"
                canCopy
              />
            </div>
          </div>
        </FormSection>

        <FormSection title="Agency Follow Up Configuration">
          <div className="grid grid-cols-3 gap-2">
            <ControlledInput
              control={form.control}
              name="firstFollowUpDuration"
              label="Days Until First Follow Up Is Due"
              isRequired
              tooltipText={`Default number of business days from request creation date until the first "Next Follow Up At" date`}
              type="number"
              validate={validatePositiveInteger}
            />

            <ControlledInput
              control={form.control}
              name="secondFollowUpDuration"
              label="Days Until Second Follow Up Is Due"
              isRequired
              tooltipText={`Default number of business days from "First Contacted At" date until the second "Next Follow Up At" date`}
              type="number"
              validate={validatePositiveInteger}
            />

            <ControlledInput
              control={form.control}
              name="thirdFollowUpDuration"
              label="Days Until Third Follow Up Is Due"
              isRequired
              tooltipText={`Default number of business days from "Second Contacted At" date until the third "Next Follow Up At" date`}
              type="number"
              validate={validatePositiveInteger}
            />

            <ControlledInput
              control={form.control}
              name="fourthFollowUpDuration"
              label="Days Until Fourth Follow Up Is Due"
              isRequired
              tooltipText={`Default number of business days from "Third Contacted At" date until the fourth "Next Follow Up At" date`}
              type="number"
              validate={validatePositiveInteger}
            />

            <ControlledInput
              control={form.control}
              name="timeoutDuration"
              label="Days Until Request Is Timed Out"
              isRequired
              tooltipText={`Default number of business days from "Fourth Contacted At" date until the request is marked as "Timed Out"`}
              type="number"
              validate={validatePositiveInteger}
            />
          </div>
        </FormSection>

        <FormSection title="Restrictions">
          <div className="grid grid-cols-2 gap-2">
            <ControlledInput
              name="urbanRestriction"
              control={form.control}
              label="Urban Area Download Restriction (acres)"
              isRequired={false}
              tooltipText="Add a download restriction for urban areas"
            />

            <ControlledInput
              name="ruralRestriction"
              control={form.control}
              label="Rural Area Download Restriction (acres)"
              isRequired={false}
              tooltipText="Add a download restriction for rural areas"
            />
          </div>
        </FormSection>

        <FormSection title="Miscellaneous">
          <ControlledTextArea
            name="notes"
            control={form.control}
            label="Notes"
            tooltipText="Notes for the Agency"
            placeholder="Add notes for this agency here"
          />
        </FormSection>

        <AgencyLogo />
        <AgencyLinks />
      </div>
    </Form>
  );
}
